import React, { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { FormButton, CancelButton } from "../../../components/Botton";
import Loader from "../../../components/Loader";
import useMasterUsers from "../useMasterUser";
import { IEditUserFormData, userValidationRules } from "../user.types";
import { useNavigate } from "react-router-dom";

interface EditMasterUserProps {
  userId: string;
}

interface EditFormWithPassword {
  name: string;
  phoneNumber: string;
  email: string;
  address?: string;
  city?: string;
  pincode?: string;
  dob?: string;
  isDisabled?: boolean;
  password?: string;
  confirmPassword?: string;
}

const EditMasterUser: React.FC<EditMasterUserProps> = ({ userId }) => {
  const { updateUser, getUserById, clearSelectedUser, loading } = useMasterUsers();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const navigate = useNavigate();
  
  // Store original values to compare changes
  const originalValues = useRef<Partial<EditFormWithPassword>>({});

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    getValues,
  } = useForm<EditFormWithPassword>();

  const password = watch("password");
  const user = getUserById(userId);

  useEffect(() => {
    if (user) {
      const initialData = {
        name: user.name || "",
        phoneNumber: user.mobileNo || "",
        email: user.email || "",
        address: user.address || "",
        city: user.city || "",
        pincode: user.pincode || "",
        dob: user.dob ? new Date(user.dob).toISOString().split('T')[0] : "",
        isDisabled: user.isDisabled || false,
      };
      
      // Store original values for comparison
      originalValues.current = { ...initialData };
      reset(initialData);
    }
  }, [user, reset]);

  // Function to get only changed fields
  const getChangedFields = (currentData: EditFormWithPassword) => {
    const changedFields: any = {};
    
    // Compare each field with original values
    Object.keys(currentData).forEach((key) => {
      const typedKey = key as keyof EditFormWithPassword;
      
      // Skip password fields if not changing password
      if ((typedKey === 'password' || typedKey === 'confirmPassword') && !changePassword) {
        return;
      }
      
      const currentValue = currentData[typedKey];
      const originalValue = originalValues.current[typedKey];
      
      // For password fields, always include both if changing password
      if (changePassword && (typedKey === 'password' || typedKey === 'confirmPassword')) {
        if (currentValue) {
          changedFields[typedKey] = currentValue;
        }
        return;
      }
      
      // Check if value has actually changed for other fields
      if (currentValue !== originalValue) {
        if (typedKey === 'phoneNumber') {
          // Map phoneNumber to mobileNo for backend
          changedFields.mobileNo = currentValue;
        } else {
          changedFields[typedKey] = currentValue;
        }
      }
    });
    
    return changedFields;
  };

  const onSubmit = async (data: EditFormWithPassword) => {
    try {
      console.log("Form data:", data);
      console.log("Original values:", originalValues.current);
      
      // Get only the changed fields
      const changedFields = getChangedFields(data);
      
      console.log("Changed fields only:", changedFields);
      
      // Only send request if there are actual changes
      if (Object.keys(changedFields).length === 0) {
        console.log("No changes detected, skipping update");
        clearSelectedUser();
        return;
      }
      
      await updateUser(userId, changedFields);
      clearSelectedUser();
    } catch (error) {
      console.error("Error updating user:", error);
    }
  };

  const handleCancel = () => {
    clearSelectedUser();
    window.location.reload();
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">User not found</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center w-full h-screen p-8">
      <div className="bg-white shadow-md rounded-lg pt-6 pb-6 w-full">
        <div className="border-b-2 w-[100%]">
          <h1 className="text-3xl mb-2 text-gray-700 ml-5">Edit User</h1>
          <p className="text-sm text-gray-500 ml-5 mb-4">
            User ID: {user._id} | Role: {user.role}
          </p>
        </div>
        
        {loading && <Loader />}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <fieldset className="m-2 rounded border border-gray-300 bg-white p-4">
            <legend className="text-lg font-semibold text-gray-700 px-2">
              Basic Information
            </legend>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              {/* Name Field */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  {...register("name", userValidationRules.name)}
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
                  placeholder="Enter full name"
                />
                {errors.name && (
                  <span className="text-red-500 text-xs italic">
                    {errors.name.message}
                  </span>
                )}
              </div>

              {/* Phone Number Field */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  {...register("phoneNumber", userValidationRules.phoneNumber)}
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
                  placeholder="Enter 10-digit phone number"
                  maxLength={10}
                />
                {errors.phoneNumber && (
                  <span className="text-red-500 text-xs italic">
                    {errors.phoneNumber.message}
                  </span>
                )}
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  {...register("email", userValidationRules.email)}
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
                  placeholder="Enter email address"
                />
                {errors.email && (
                  <span className="text-red-500 text-xs italic">
                    {errors.email.message}
                  </span>
                )}
              </div>

              {/* Date of Birth Field */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2">
                  Date of Birth
                </label>
                <input
                  type="date"
                  {...register("dob")}
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
                />
              </div>
            </div>
          </fieldset>

   

          {/* Account Status */}
          <fieldset className="m-2 rounded border border-gray-300 bg-yellow-50 p-4">
            <legend className="text-lg font-semibold text-gray-700 px-2">
              Account Status & Password
            </legend>
            
            <div className="mt-4 space-y-4">
              {/* User Role (Read-only) */}
              <div>
                <label className="block text-gray-700 text-base font-semibold mb-2">
                  User Role (Read-only)
                </label>
                <input
                  type="text"
                  value={user.role}
                  readOnly
                  className="appearance-none border rounded w-full py-2 px-3 text-gray-500 bg-gray-100 leading-tight cursor-not-allowed"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Role cannot be changed from this interface
                </p>
              </div>

              {/* Status Toggle */}
              {/* <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register("isDisabled")}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                />
                <label className="text-gray-700 text-base font-semibold">
                  Disable User Account
                </label>
              </div>
              <p className="text-xs text-gray-600">
                Disabled users cannot log in or access the system
              </p> */}

              {/* Password Change Section */}
              <div className="border-t pt-4">
                <div className="mb-4">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={changePassword}
                      onChange={(e) => {
                        setChangePassword(e.target.checked);
                        if (!e.target.checked) {
                          setValue("password", "");
                          setValue("confirmPassword", "");
                        }
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="text-gray-700 text-base font-semibold">
                      Change Password
                    </label>
                  </div>
                </div>

                {changePassword && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* New Password Field */}
                    <div>
                      <label className="block text-gray-700 text-base font-semibold mb-2">
                        New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          {...register("password", {
                            required: changePassword ? "Password is required" : false,
                            minLength: changePassword ? {
                              value: 8,
                              message: "Password must be at least 8 characters",
                            } : undefined,
                            pattern: changePassword ? {
                              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/,
                              message: "Password must contain uppercase, lowercase, number and special character",
                            } : undefined,
                          })}
                          className="appearance-none border rounded w-full py-2 px-3 pr-10 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
                          placeholder="Enter new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                        >
                          {showPassword ? "🙈" : "👁️"}
                        </button>
                      </div>
                      {errors.password && (
                        <span className="text-red-500 text-xs italic">
                          {errors.password.message}
                        </span>
                      )}
                    </div>

                    {/* Confirm New Password Field */}
                    <div>
                      <label className="block text-gray-700 text-base font-semibold mb-2">
                        Confirm New Password <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          {...register("confirmPassword", {
                            required: changePassword ? "Please confirm your password" : false,
                            validate: changePassword ? (value) =>
                              value === password || "Passwords do not match" : undefined,
                          })}
                          className="appearance-none border rounded w-full py-2 px-3 pr-10 text-gray-600 leading-tight focus:outline-none focus:shadow-outline focus:border-blue-500"
                          placeholder="Confirm new password"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5"
                        >
                          {showConfirmPassword ? "🙈" : "👁️"}
                        </button>
                      </div>
                      {errors.confirmPassword && (
                        <span className="text-red-500 text-xs italic">
                          {errors.confirmPassword.message}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Additional User Info (Read-only) */}
              {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1">
                    Demo Account
                  </label>
                  <span className={`px-2 py-1 rounded text-xs ${
                    user.isDemo 
                      ? 'bg-yellow-100 text-yellow-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {user.isDemo ? 'Yes' : 'No'}
                  </span>
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1">
                    Language
                  </label>
                  <span className="text-gray-600 text-sm">
                    {user.languageName || 'Not Set'}
                  </span>
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1">
                    Course
                  </label>
                  <span className="text-gray-600 text-sm">
                    {user.courseName || 'Not Set'}
                  </span>
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1">
                    Standard
                  </label>
                  <span className="text-gray-600 text-sm">
                    {user.standard || 'Not Set'}
                  </span>
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1">
                    Joined Date
                  </label>
                  <span className="text-gray-600 text-sm">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </span>
                </div>
                
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-1">
                    Last Updated
                  </label>
                  <span className="text-gray-600 text-sm">
                    {new Date(user.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div> */}

              {/* User Stats */}
           
            </div>
          </fieldset>

          {/* Action Buttons */}
          <div className="flex justify-start mt-6 ml-10">
            <FormButton
              className="py-2 px-4 rounded mr-4"
              type="submit"
              children={loading ? "Updating..." : "Update User"}
            />
            <CancelButton
              className="py-2 px-4 rounded"
              onClick={handleCancel}
              children="Cancel"
              type="button"
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditMasterUser;